import type { DiscountTier, MasterItem, Perm, ProductPhoto, Role } from '@sj/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { tracker } from '../features/driver/tracker.js';
import { api, onSessionExpired } from '../lib/api.js';
import { configureMap } from '../lib/map.js';
import type { Me } from '../lib/session.js';

// Sesi aplikasi: pengguna login, meta (/api/meta), hitungan lencana menu (owner/admin) — pengganti A.me/A.meta/A.state v1.

export interface MetaProduct {
  id: number;
  code: string;
  name: string;
  unit: string;
  price: number;
  unit2: string;
  price2: number;
  unit2_factor: number;
  division: string;
  stock?: number;
  cost_avg?: number;
  active?: boolean;
  /** Foto produk (maks. 3 slot) — Produk & Harga. */
  photos?: ProductPhoto[];
  [k: string]: unknown;
}

/** Pengguna aktif ringkas di /api/meta (drivers: + phone). */
export interface MetaUser {
  id: number;
  name: string;
  phone?: string;
}

export interface Meta {
  settings: Record<string, string> & { business_name: string };
  /** Data Master: pilihan aktif (satuan, divisi, area, kategori biaya, rekening, alasan, …). */
  master: MasterItem[];
  roles: Record<Role, string>;
  products: MetaProduct[];
  discount_tiers: DiscountTier[];
  /** Driver aktif — hanya owner/admin (v1 A.meta.drivers). */
  drivers?: MetaUser[];
  /** Sales aktif — hanya owner/admin (v1 A.meta.sales). */
  sales?: MetaUser[];
  /** Toko milik akun — hanya akun toko (null bila tidak ditemukan). */
  store?: {
    id: number;
    code?: string;
    name: string;
    owner_name?: string;
    address: string;
    area: string;
    phone?: string;
    term_days: number;
    lat: number | null;
    lng: number | null;
  } | null;
  today?: string;
  [k: string]: unknown;
}

export interface Counts {
  spo?: number;
  nota?: number;
  keu?: number;
  /** Kunjungan: sales = permintaan toko baru; owner/admin = kunjungan janggal belum dicek. */
  kunj?: number;
  /** Akun toko: kunjungan sales yang belum dikonfirmasi. */
  konf?: number;
}

interface Session {
  me: Me;
  meta: Meta;
  counts: Counts;
  /** Jenis dasar owner/admin (cakupan data penuh). Untuk izin aksi pakai can(). */
  isMgr: boolean;
  /** Punya hak ini? Owner selalu ya. */
  can: (p: Perm) => boolean;
  refreshCounts: () => Promise<void>;
  reloadMeta: () => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<Session | null>(null);
export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error('useSession di luar <SessionProvider>');
  return s;
}

export const productById = (meta: Meta, id: unknown) => meta.products.find((p) => p.id === Number(id));

export function SessionProvider({
  me,
  onLogout,
  children,
}: {
  me: Me;
  /** msg = notifikasi setelah keluar (logout biasa / sesi berakhir). */
  onLogout: (msg?: string) => void;
  children: ReactNode;
}) {
  const qc = useQueryClient();
  const isMgr = me.role === 'owner' || me.role === 'admin';
  const can = useCallback((p: Perm) => me.role === 'owner' || !!me.perms?.includes(p), [me.role, me.perms]);
  const meta = useQuery({
    queryKey: ['meta', me.id],
    queryFn: () => api<Meta>('GET', '/api/meta'),
    staleTime: Number.POSITIVE_INFINITY,
  });
  const [counts, setCounts] = useState<Counts>({});

  // Password sementara: semua API ditolak (428) sampai diganti → jangan memuat apa pun dulu.
  const pending = !!me.must_change_pw;
  const refreshCounts = useCallback(async () => {
    if (pending) return;
    try {
      if (me.role === 'toko') {
        const t = await api<{ visit_confirm: number }>('GET', '/api/toko/notif');
        setCounts({ konf: t.visit_confirm });
        return;
      }
      if (me.role === 'sales') {
        const n = await api<{ visit_req: number }>('GET', '/api/notif');
        setCounts({ kunj: n.visit_req });
        return;
      }
      if (!isMgr) return;
      // Lencana per menu hanya untuk menu yang boleh dibuka peran ini.
      const [d, n] = await Promise.all([
        can('beranda.lihat') || can('operasi.lihat')
          ? api<{ counts: Record<string, number> }>('GET', '/api/reports/dashboard')
          : Promise.resolve({ counts: {} as Record<string, number> }),
        can('kunjungan.periksa')
          ? api<{ visit_check: number }>('GET', '/api/notif').catch(() => ({ visit_check: 0 }))
          : Promise.resolve({ visit_check: 0 }),
      ]);
      const c = d.counts;
      setCounts({
        spo: can('spo.verifikasi') ? c.spo_diajukan : 0,
        nota: can('nota.lihat') ? (c.disiapkan ?? 0) + (c.terkirim ?? 0) + (c.gagal ?? 0) : 0,
        keu: can('keuangan.transfer') ? c.transfer_menunggu : 0,
        kunj: n.visit_check,
      });
    } catch {
      /* abaikan */
    }
  }, [isMgr, me.role, can, pending]);

  // Hak akses bisa diubah owner kapan saja: cek ulang tiap 60 detik (saat tab terlihat) → menu & tombol ikut berubah.
  useEffect(() => {
    const t = setInterval(async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const u = (await api<{ user: Me | null }>('GET', '/api/auth/me')).user;
        if (u && JSON.stringify([u.role, u.role_name, u.perms]) !== JSON.stringify([me.role, me.role_name, me.perms])) {
          qc.setQueryData(['me'], u);
        }
      } catch {
        /* sesi habis ditangani onSessionExpired */
      }
    }, 60000);
    return () => clearInterval(t);
  }, [qc, me.role, me.role_name, me.perms]);

  // Lencana menu diperbarui tiap 60 detik (saat tab terlihat).
  useEffect(() => {
    void refreshCounts();
    const t = setInterval(() => {
      if (document.visibilityState === 'visible') void refreshCounts();
    }, 60000);
    return () => clearInterval(t);
  }, [refreshCounts]);

  useEffect(() => onSessionExpired(() => onLogout('Sesi Anda berakhir — silakan masuk lagi.')), [onLogout]);

  const logout = useCallback(async () => {
    tracker.stop();
    try {
      await api('POST', '/api/auth/logout');
    } catch {
      /* tetap keluar */
    }
    qc.clear();
    onLogout('Anda sudah keluar.');
  }, [qc, onLogout]);

  const reloadMeta = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ['meta', me.id] });
  }, [qc, me.id]);

  // Titik awal peta dari Pengaturan (lokasi gudang/kantor).
  if (meta.data) configureMap(meta.data.settings);
  // Sama dengan v1: sebelum meta termuat, layar aplikasi belum ditampilkan.
  if (!meta.data) return null;
  return (
    <Ctx.Provider value={{ me, meta: meta.data, counts, isMgr, can, refreshCounts, reloadMeta, logout }}>{children}</Ctx.Provider>
  );
}

/**
 * Keluar dengan konfirmasi bila perjalanan (driver) / berbagi lokasi (sales) masih aktif — GPS berhenti saat keluar.
 * confirmBox dari useConfirm() pemanggil (modal ada di dalam SessionProvider).
 */
export function useLogout(
  confirmBox: (title: string, text: ReactNode, o?: { ok?: string; danger?: boolean }) => Promise<string | true | null>,
) {
  const { logout } = useSession();
  return useCallback(async () => {
    if (tracker.active) {
      const ok = await confirmBox(
        'Keluar sekarang?',
        'Perjalanan / berbagi lokasi masih aktif. Setelah keluar, posisi Anda tidak lagi terkirim ke admin.',
        { ok: 'Keluar', danger: true },
      );
      if (ok !== true) return;
    }
    await logout();
  }, [confirmBox, logout]);
}
